import { I_FilterItem, priceType } from '@Redux/search/interfaces';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-tw';
import { lionDomain } from '@utils/domainByEnv';

interface I_UpdateFilterProps {
    data: I_FilterItem[];
    nowType: string;
    text: string;
    value: number | string;
    subValue: string;
    priceDefaultRange?: number[]; // 價錢初始最大最小值
}

/** @summary 篩選掉目前傳入的item
 * @param props I_UpdateFilterProps
 * @returns 剩下未被篩選掉的item
 */
export const updateFilterItems = (props: I_UpdateFilterProps) => {
    const { data, nowType, value, text, subValue, priceDefaultRange } = props;
    const newItems = JSON.parse(JSON.stringify(data));

    switch (subValue) {
        case priceType.min:
        case priceType.max: {
            const isMatchPrice = newItems.filter((item: I_FilterItem) => {
                const isSameSubValue = item.subValue === subValue;
                return isSameSubValue;
            });
            // 如果是相同min or max 則更新成新值
            newItems.forEach((item: I_FilterItem, idx: number, arr: []) => {
                const istSameType = item.filterBy === nowType;
                const isSameSubValue = item.subValue === subValue;
                if (istSameType && isSameSubValue) {
                    const initMin = priceDefaultRange && priceDefaultRange[0];
                    const initMax = priceDefaultRange && priceDefaultRange[1];
                    if (subValue === priceType.min && value === initMin) {
                        arr.splice(idx, 1);
                    } else if (subValue === priceType.max && value === initMax) {
                        arr.splice(idx, 1);
                    } else {
                        item.text = text;
                        item.value = value;
                    }
                }
            });

            if (isMatchPrice.length === 0) {
                // 無選到已有選項則新增一個
                newItems.push({
                    filterBy: nowType,
                    text,
                    value,
                    subValue,
                });
            }
            return newItems;
        }

        default: {
            // 回傳目前點到的項目是否已在目前篩選陣列中
            const isMatch = newItems.filter((item: I_FilterItem) => {
                return item.filterBy === nowType && item.value == value;
            });

            // 回傳所有 非相同類別 或 同類別但不同值
            const notMatchItem = newItems.filter((item: I_FilterItem) => {
                const istSameType = item.filterBy === nowType;
                const isSameValue = item.value == value;
                return !istSameType || (istSameType && !isSameValue);
            });
            if (isMatch.length === 0) {
                // 無選到已有選項則新增一個
                newItems.push({
                    filterBy: nowType,
                    text,
                    value,
                    subValue,
                });
                return newItems;
            } else {
                // 選到已有選項則回傳已被篩掉重複的選項陣列
                return notMatchItem;
            }
        }
    }
};

/** @summary 將價格數字每三位數加逗號
 * @param price 輸入需轉換的數字
 * @returns 數字string 1,000,000
 */
export const formatPrice = (price: number) => {
    return new Intl.NumberFormat().format(price);
};

/** @summary 將文案限制顯示上限字數
 * @param text 需判斷的文案
 * @param limitLength 設定上限字數量
 * @returns 超過設定數量則會擷取後在最後接上 "..."
 */
export const limitTextLength = (text: string, limitLength: number) => {
    if (text.length > limitLength) return `${text.slice(0, limitLength)}...`;
    return text;
};

const ReorganizationUrl = (getUrl: string, params: { [key: string]: string }) => {
    const paramsName: { [key: string]: string } = {
        normgroupid: 'NormGroupID',
        groupid: 'GroupID',
        tourid: 'TourID',
    };
    const tempUrl = decodeURIComponent(getUrl).replace(' ', '').replace('?', '').split('&');
    let tempParams: any = {};
    let url = '';

    tempUrl.length > 0 &&
        tempUrl[0] &&
        tempUrl.forEach(function (item) {
            const tempItem = item.split('=');
            let itemName = tempItem[0];
            if (paramsName[itemName.toLowerCase()]) {
                itemName = paramsName[itemName.toLowerCase()];
            }
            tempParams[itemName] = tempItem[1];
        });

    for (const param in params) {
        const key = param.toLowerCase();
        if (paramsName[key]) {
            params[paramsName[key]] = params[param];
        }
    }
    tempUrl.length > 0 ? (tempParams = { ...tempParams, ...params }) : (tempParams = { ...params });

    for (const key in tempParams) {
        if (key != 'IsPreview' && key != 'IsEnsureGroup') {
            url += (url != '' ? '&' : '') + key + '=' + tempParams[key];
        }
    }

    return url;
};

/** @summary 重組網址
 * @param NormGroupID 該團NormGroupID
 * @param GroupID 該團GroupID
 * @returns detail.html?NormGroupID=xxxxx&GroupID=xxxxxx
 */
interface I_ChangeDetailHistory {
    NormGroupID: string;
    GroupID: string;
    TourID?: string;
}

export const changeDetailHistory = ({ NormGroupID, GroupID, TourID }: I_ChangeDetailHistory) => {
    history.pushState(
        null,
        '',
        '?' +
            ReorganizationUrl(location.search, {
                NormGroupID: NormGroupID,
                GroupID: GroupID,
            })
    );
};

/** @summary 將XX:XX時間格式轉為XX小時XX分鐘格式
 * @param time 需判斷的時間
 * @returns 3小時30分鐘
 */
export const timeTransform = (time: string) => {
    const splitTime = time.split(':');
    const splitHour = splitTime[0] === '00' || splitTime[0] === '0' ? '' : splitTime[0];
    const splitMin = splitTime[1] === '00' || splitTime[1] === '0' ? '' : splitTime[1];
    const hour = splitHour ? splitHour + '小時' : '';
    const min = splitMin ? splitMin + '分鐘' : '';
    return hour + min;
};

/** @summary 將 2023-01-30 11:22:28 時間格式轉為 2023/01/30 11:22:28 格式
 * @param time 需 format 的時間
 * @returns 2021/12/08 10:04:36
 */
export const formatTime = (time: string | Date) => {
    return dayjs(time).format('YYYY/MM/DD HH:mm:ss');
};

/** @summary 利用日期算出是星期幾
 * @param date 2023/10/31
 * @param fullDate 默認 true
 * @returns true -> 2023/10/31 (一)、false -> 10/31 (一)
 */
export const getWeekday = (date: string, fullDate: boolean = true) => {
    const weekDay = dayjs(date).locale('zh-tw').format('dd');
    return fullDate ? `${date} (${weekDay})` : `${dayjs(date).format('MM/DD')} (${weekDay})`;
};

/** @summary 組 url query 參數，若參數為空值則不加入字串中
 * @param params {NormGroupID:bdcb8b54-a1c2-4e74-9d4b-d4de0aa405ad;GroupID:24JK401TEST-T;PreferAirlines:''}
 * @returns NormGroupID=bdcb8b54-a1c2-4e74-9d4b-d4de0aa405ad&GroupID=24JK401TEST-T
 */
export const buildQueryString = (params: { [key: string]: string }) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
    });
    return queryParams.toString();
};

// 取得航空公司 icon URL
export const getIconImageURL = (airlineCode?: string) => {
    if (!airlineCode) return `https:${lionDomain}/Images/carr/default.png`;
    return `https:${lionDomain}/Images/carr/${airlineCode}.png`
};
